import styled, { keyframes } from 'styled-components'

const pageInForward = keyframes`
  from { opacity: 0; transform: translateX(8px); }
  to   { opacity: 1; transform: translateX(0); }
`

const pageInBack = keyframes`
  from { opacity: 0; transform: translateX(-8px); }
  to   { opacity: 1; transform: translateX(0); }
`

export const Container = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgb(216, 216, 216);
  box-shadow:
    rgba(0, 0, 0, 0.02) 0px 3px 6px -2px,
    rgba(0, 0, 0, 0.04) 0px 1px 1px;
  width: 100%;
  overflow: hidden;
  font-family: var(--Font-family-font-family-body, Poppins), serif;
  margin: 12px 0;
  outline: none;
`

export const Section = styled.div`
  padding: 16px;
`

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
`

export const HeaderLeft = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex: 1;
  min-width: 0;
`

export const HeaderChip = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: #737373;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 2px 6px;
  white-space: nowrap;
  flex-shrink: 0;
`

export const QuestionText = styled.div.attrs({
  role: 'heading',
  'aria-level': 3,
})`
  font-size: 14px;
  font-weight: 500;
  color: #171717;
  margin: 0;
  line-height: 1.4;
`

export const NavCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`

export const NavButton = styled.button`
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  cursor: pointer;
  color: #737373;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 0;
  transition: background 0.12s ease;

  &:hover:not(:disabled) {
    background: #f5f5f5;
    color: #171717;
  }

  &:disabled {
    color: #d4d4d4;
    cursor: default;
  }
`

export const NavCounter = styled.span`
  font-size: 12px;
  color: #737373;
  padding: 0 4px;
  white-space: nowrap;
  user-select: none;
`

export const QuestionPage = styled.div<{ $direction: 'forward' | 'back' }>`
  animation: ${({ $direction }) =>
      $direction === 'forward' ? pageInForward : pageInBack}
    0.14s ease both;
`

export const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const OptionRow = styled.div<{
  $keyboardFocused: boolean
  $selected: boolean
  $readOnly: boolean
}>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 4px;
  cursor: ${({ $readOnly }) => ($readOnly ? 'default' : 'pointer')};
  background: ${({ $keyboardFocused, $selected, $readOnly }) =>
    $selected
      ? '#f0f0f0'
      : !$readOnly && $keyboardFocused
        ? '#f5f5f5'
        : 'transparent'};
  transition: background 0.1s ease;

  &:hover {
    background: ${({ $readOnly, $selected }) =>
      $readOnly ? ($selected ? '#f0f0f0' : 'transparent') : '#f5f5f5'};
  }
`

export const OptionNumber = styled.div<{
  $keyboardFocused: boolean
  $selected: boolean
}>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #e5e5e5;
  color: ${({ $keyboardFocused, $selected }) =>
    $selected || $keyboardFocused ? '#171717' : '#a3a3a3'};
  font-size: 11px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const OptionBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`

export const OptionLabel = styled.span<{
  $keyboardFocused: boolean
  $selected: boolean
}>`
  font-size: 13px;
  color: ${({ $keyboardFocused, $selected }) =>
    $selected || $keyboardFocused ? '#171717' : '#737373'};
`

export const OptionDescription = styled.span`
  font-size: 12px;
  color: #737373;
  line-height: 1.4;
`

export const TrailingIcon = styled.div`
  color: #737373;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const PreviewBox = styled.pre`
  margin: 0 0 4px 32px;
  padding: 10px 12px;
  background: #f7f7f7;
  border-radius: 8px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  color: #525252;
  white-space: pre-wrap;
  overflow-x: auto;
`

export const PencilIcon = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  background: #e5e5e5;
  color: #a3a3a3;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const StyledInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 13px;
  color: #171717;
  outline: none;
  font-family: inherit;

  &::placeholder {
    color: #a3a3a3;
    font-style: italic;
  }

  &:disabled {
    cursor: default;
  }
`

export const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding-top: 12px;
`

export const SubmitButton = styled.button<{ $enabled: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: ${({ $enabled }) => ($enabled ? '#171717' : '#d4d4d4')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: ${({ $enabled }) => ($enabled ? 'pointer' : 'default')};
  transition: background 0.15s ease;

  &:hover {
    background: ${({ $enabled }) => ($enabled ? '#f5f5f5' : 'transparent')};
  }
`
